import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import query from '@/api/APIServicesV2'
import { ProductDetailsResponse } from '../interfaces/inventory'

export interface BatchesQueryParams {
  page?: number
  limit?: number
  search?: string
  entryDateFrom?: string | null
  entryDateTo?: string | null
  expirationDateFrom?: string | null
  expirationDateTo?: string | null
  availableQuantityFrom?: number | null
  availableQuantityTo?: number | null
  purchaseCostFrom?: number | null
  purchaseCostTo?: number | null
  buyedReceiptId?: number | null
  areaIds?: number[] | null
  supplierId?: number | null
  productPriceFrom?: number | null
  productPriceTo?: number | null
}

export interface UseProductDetailsOptions
  extends Omit<
    UseQueryOptions<ProductDetailsResponse, AxiosError>,
    'queryKey' | 'queryFn'
  > {
  productId: number | null
  params?: BatchesQueryParams
}

// Create a unique, descriptive key for this query
const PRODUCT_DETAILS_QUERY_KEY = 'product-details'

/**
 * API function to fetch product batches
 */
const fetchProductBatches = async (
  productId: number,
  params?: BatchesQueryParams
): Promise<ProductDetailsResponse> => {
  const searchParams = new URLSearchParams()

  if (params) {
    const {
      page,
      limit,
      search,
      entryDateFrom,
      entryDateTo,
      expirationDateFrom,
      expirationDateTo,
      availableQuantityFrom,
      availableQuantityTo,
      purchaseCostFrom,
      purchaseCostTo,
      buyedReceiptId,
      areaIds,
      supplierId,
      productPriceFrom,
      productPriceTo,
    } = params

    if (page) searchParams.set('page', String(page))
    if (limit) searchParams.set('limit', String(limit))
    if (search && search.trim()) searchParams.set('search', search.trim())

    if (entryDateFrom) searchParams.set('entryDateFrom', entryDateFrom)
    if (entryDateTo) searchParams.set('entryDateTo', entryDateTo)
    if (expirationDateFrom)
      searchParams.set('expirationDateFrom', expirationDateFrom)
    if (expirationDateTo) searchParams.set('expirationDateTo', expirationDateTo)

    if (availableQuantityFrom != null)
      searchParams.set('availableQuantityFrom', String(availableQuantityFrom))
    if (availableQuantityTo != null)
      searchParams.set('availableQuantityTo', String(availableQuantityTo))
    if (purchaseCostFrom != null)
      searchParams.set('purchaseCostFrom', String(purchaseCostFrom))
    if (purchaseCostTo != null)
      searchParams.set('purchaseCostTo', String(purchaseCostTo))

    if (buyedReceiptId != null)
      searchParams.set('buyedReceiptId', String(buyedReceiptId))

    if (Array.isArray(areaIds) && areaIds.length > 0) {
      areaIds.forEach((id) => searchParams.append('areaIds', String(id)))
    }

    if (supplierId != null) searchParams.set('supplierId', String(supplierId))
    if (productPriceFrom != null)
      searchParams.set('productPriceFrom', String(productPriceFrom))
    if (productPriceTo != null)
      searchParams.set('productPriceTo', String(productPriceTo))
  }

  const qs = searchParams.toString()
  const url = `/containers/products/${productId}/batches${qs ? `?${qs}` : ''}`

  const response = await query.get(url)
  return response.data as ProductDetailsResponse
}

/**
 * Custom hook to fetch product batches only
 *
 * @param options Configuration options including productId and react-query options
 * @returns TanStack React Query result with product batches
 */
export const useProductDetails = ({
  productId,
  params,
  enabled = true,
  staleTime = 5 * 60 * 1000, // 5 minutes
  ...options
}: UseProductDetailsOptions) => {
  return useQuery({
    queryKey: [
      PRODUCT_DETAILS_QUERY_KEY,
      productId,
      // Serializar parámetros para evitar re-renders innecesarios
      JSON.stringify(params || {}),
    ],
    queryFn: () => {
      if (!productId) {
        throw new Error('Product ID is required')
      }
      return fetchProductBatches(productId, params)
    },
    enabled: enabled && productId !== null,
    staleTime,
    // Configuraciones adicionales para evitar bucles
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    ...options,
  })
}

/**
 * Helper function to get the product details query key
 * Useful for cache invalidation or prefetching
 */
export const getProductDetailsQueryKey = (productId: number) => [
  PRODUCT_DETAILS_QUERY_KEY,
  productId,
]
